<template>
  <div class="content consentements-page">
    <div class="row mt-5">
      <div class="col-md-12">
        <h3 class="mb-1">ESPACE CONSENTEMENTS</h3>
        <p class="text-muted consentements-subtitle">
          Centralisation et traçabilité des consentements collectés sur l'ensemble de vos flux Weedo IT.
        </p>

        <!-- KPI -->
        <div class="row mb-4" v-if="kpis">
          <div class="col-xl-3 col-md-6 mb-3">
            <div class="card consent-kpi consent-kpi--collected">
              <div class="card-body">
                <div class="consent-kpi__label">Consentements collectés</div>
                <div class="consent-kpi__value">{{ formatNumber(kpis.collected.count) }}</div>
                <div class="consent-kpi__pct">&nbsp;</div>
              </div>
            </div>
          </div>
          <div class="col-xl-3 col-md-6 mb-3">
            <div class="card consent-kpi consent-kpi--active">
              <div class="card-body">
                <div class="consent-kpi__label">Consentements actifs</div>
                <div class="consent-kpi__value">{{ formatNumber(kpis.active.count) }}</div>
                <div class="consent-kpi__pct" v-if="kpis.active.percent != null">{{ kpis.active.percent }} %</div>
              </div>
            </div>
          </div>
          <div class="col-xl-3 col-md-6 mb-3">
            <div class="card consent-kpi consent-kpi--withdrawn">
              <div class="card-body">
                <div class="consent-kpi__label">Consentements retirés</div>
                <div class="consent-kpi__value">{{ formatNumber(kpis.withdrawn.count) }}</div>
                <div class="consent-kpi__pct" v-if="kpis.withdrawn.percent != null">{{ kpis.withdrawn.percent }} %</div>
              </div>
            </div>
          </div>
          <div class="col-xl-3 col-md-6 mb-3">
            <div class="card consent-kpi consent-kpi--expired">
              <div class="card-body">
                <div class="consent-kpi__label">Consentements expirés</div>
                <div class="consent-kpi__value">{{ formatNumber(kpis.expired.count) }}</div>
                <div class="consent-kpi__pct" v-if="kpis.expired.percent != null">{{ kpis.expired.percent }} %</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Filtres -->
        <div class="card mb-4">
          <div class="card-body">
            <div class="row">
              <base-input class="col-md-4 mb-2" label="Date début">
                <el-date-picker
                  v-model="filters.date_from"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder="YYYY-MM-DD"
                  :clearable="false"
                  :picker-options="startPickerOptions"
                  class="w-100"
                />
              </base-input>
              <base-input class="col-md-4 mb-2" label="Date fin">
                <el-date-picker
                  v-model="filters.date_to"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder="YYYY-MM-DD"
                  :clearable="false"
                  :picker-options="endPickerOptions"
                  class="w-100"
                />
              </base-input>
              <base-input label="Produit" class="col-md-4 mb-2">
                <el-select v-model="filters.product" class="select-primary w-100" clearable placeholder="Tous">
                  <el-option
                    v-for="opt in filtersMeta.products"
                    :key="opt.value || 'all'"
                    :value="opt.value"
                    :label="opt.label"
                  />
                </el-select>
              </base-input>
              <base-input label="Type de flux (pack)" class="col-md-4 mb-2">
                <el-select v-model="filters.flow_type" class="select-primary w-100" clearable placeholder="Tous">
                  <el-option
                    v-for="opt in filtersMeta.flow_types"
                    :key="opt.value || 'all-ft'"
                    :value="opt.value"
                    :label="opt.label"
                  />
                </el-select>
              </base-input>
              <base-input label="Statut" class="col-md-4 mb-2">
                <el-select v-model="filters.status" class="select-primary w-100" clearable placeholder="Tous">
                  <el-option
                    v-for="opt in filtersMeta.consent_statuses"
                    :key="opt.value || 'all-st'"
                    :value="opt.value"
                    :label="opt.label"
                  />
                </el-select>
              </base-input>
              <base-input label="Consommateur" class="col-md-4 mb-2">
                <el-input
                  v-model="filters.search"
                  clearable
                  placeholder="Nom, email, tél"
                />
              </base-input>
              <div class="col-12 mb-2 consent-filter-actions">
                <div class="consent-filter-actions__btns">
                  <button
                    v-if="loading"
                    type="button"
                    class="btn-consent"
                    disabled
                  >CHARGEMENT...</button>
                  <button
                    v-else
                    type="button"
                    class="btn-consent"
                    @click="refetchConsents"
                  >RECHERCHER</button>
                  <button
                    type="button"
                    class="btn btn-default"
                    :disabled="loading"
                    @click="resetFilters"
                  >RÉINITIALISER</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tableau -->
        <div class="card">
          <div class="card-body">
            <div class="row mb-3 align-items-center">
              <div class="col-md-6">
                <button type="button" class="btn-consent" @click="onExportCsv">
                  <i class="tim-icons icon-cloud-download-93"></i> EXPORT CSV
                </button>
                <base-checkbox inline class="ml-3 mb-0" v-model="exportWithProof">
                  avec les champs de preuve
                </base-checkbox>
              </div>
              <div class="col-md-6 text-md-right">
                <base-input class="d-inline-block mb-0" style="min-width: 120px">
                  <el-select v-model="pagination.perPage" class="select-primary">
                    <el-option v-for="n in pagination.perPageOptions" :key="n" :label="n" :value="n" />
                  </el-select>
                </base-input>
                <span class="small text-muted ml-2">lignes / page</span>
              </div>
            </div>

            <el-table
              :data="paginatedItems"
              v-loading="loading"
              :empty-text="loading ? ' ' : 'Aucune donnée'"
              class="customtable_leads consentements-table"
              @sort-change="onSortChange"
              @row-click="openDetail"
            >
              <el-table-column label="Date & heure de collecte" min-width="130" sortable="custom" prop="collected_at">
                <template slot-scope="scope">
                  <div>{{ formatCollectedDate(scope.row) }}</div>
                  <div class="small text-muted">{{ formatCollectedTime(scope.row) }}</div>
                </template>
              </el-table-column>
              <el-table-column label="Consommateur" min-width="280" width="280">
                <template slot-scope="scope">
                  <div class="small consent-consumer-name text-muted font-weight-bold">
                    {{ scope.row.consumer.last_name }} {{ scope.row.consumer.first_name }}
                  </div>
                  <div class="small consent-consumer-email">{{ scope.row.consumer.email }}</div>
                  <div
                    v-if="scope.row.consent.status === 'ACTIVE'"
                    class="small consent-consumer-phone"
                  >{{ formatPhone(scope.row.consumer.phone) }}</div>
                </template>
              </el-table-column>
              <el-table-column label="Produit" prop="collection.product" min-width="80" align="center" sortable="custom">
                <template slot-scope="scope">
                  <i
                    :class="(icons[(scope.row.collection.product || '').replace(/Ã©/g, 'é')] || 'fas fa-tag') + ' icon-large'"
                    :title="(scope.row.collection.product || 'Produit').toUpperCase()"
                  ></i>
                </template>
              </el-table-column>
              <el-table-column label="Type" min-width="120">
                <template slot-scope="scope">
                  {{ flowTypeLabel(scope.row.collection.flow_type) }}
                </template>
              </el-table-column>
              <el-table-column label="Origine du support" min-width="115">
                <template slot-scope="scope">
                  {{ scope.row.collection.support_origin.domain || 'Non renseigné' }}
                </template>
              </el-table-column>
              <el-table-column label="Statut" min-width="110" align="center" sortable="custom" prop="status">
                <template slot-scope="scope">
                  <button
                    v-if="canWithdrawConsent(scope.row)"
                    type="button"
                    class="consent-status-pill consent-status-pill--btn"
                    :class="[
                      statusConfig(scope.row.consent.status).pillClass,
                      { 'is-loading': isWithdrawingRow(scope.row) }
                    ]"
                    :disabled="withdrawLoading"
                    :title="isWithdrawingRow(scope.row) ? 'Retrait en cours…' : 'Retirer le consentement'"
                    @click.stop="confirmWithdrawConsent(scope.row)"
                  >
                    <i
                      v-if="isWithdrawingRow(scope.row)"
                      class="fas fa-spinner fa-spin consent-status-pill__spinner"
                      aria-hidden="true"
                    ></i>
                    <template v-else>
                      {{ statusConfig(scope.row.consent.status).labelUpper }}
                    </template>
                  </button>
                  <span
                    v-else
                    class="consent-status-pill"
                    :class="statusConfig(scope.row.consent.status).pillClass"
                  >
                    {{ statusConfig(scope.row.consent.status).labelUpper }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Preuve" min-width="110" width="120" align="center">
                <template slot-scope="scope">
                  <button
                    type="button"
                    class="btn-consent btn-consent--icon"
                    title="Voir la preuve"
                    @click.stop="openDetail(scope.row)"
                  >
                    <i class="tim-icons icon-zoom-split"></i>
                  </button>
                </template>
              </el-table-column>
              <el-table-column label="Expiration du consentement" min-width="160" align="center" sortable="custom" prop="expires_at">
                <template slot-scope="scope">
                  {{
                    scope.row.consent.status === 'WITHDRAWN'
                      ? '—'
                      : formatIsoDate(scope.row.consent.expires_at)
                  }}
                </template>
              </el-table-column>
              <el-table-column label="Retrait – date & heure" min-width="115">
                <template slot-scope="scope">
                  <template v-if="scope.row.withdrawal.withdrawn && scope.row.withdrawal.withdrawn_at">
                    {{ formatIsoDateTime(scope.row.withdrawal.withdrawn_at) }}
                  </template>
                  <template v-else>—</template>
                </template>
              </el-table-column>
            </el-table>

            <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap mt-3">
              <p class="card-category">
                Affichage {{ paginationFrom + 1 }} à {{ paginationTo }} sur {{ filteredItems.length }} entrées
              </p>
              <base-pagination
                type="market"
                class="pagination-no-border"
                v-model="pagination.currentPage"
                :per-page="pagination.perPage"
                :total="filteredItems.length"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Panneau preuve latéral (maquette Figma Lot 1) -->
    <div
      class="consent-drawer-backdrop"
      :class="{ 'is-open': showDetailPanel }"
      @click="closeDetailPanel"
    ></div>
    <aside
      class="consent-drawer"
      :class="{ 'is-open': showDetailPanel }"
      role="dialog"
      aria-modal="true"
      aria-label="Preuve du consentement"
    >
      <div v-if="selectedConsent" class="consent-drawer__content">
        <header class="consent-drawer__header">
          <div class="consent-drawer__header-left">
            <span class="consent-drawer__header-icon">
              <i class="tim-icons icon-check-2"></i>
            </span>
            <div>
              <div class="consent-drawer__eyebrow">CONSENTEMENT</div>
              <div class="consent-drawer__title">#{{ selectedConsent.consent_id }}</div>
            </div>
          </div>
          <button
            type="button"
            class="consent-drawer__close"
            title="Fermer"
            @click="closeDetailPanel"
          >
            <i class="tim-icons icon-simple-remove"></i>
          </button>
        </header>

        <div class="consent-drawer__scroll">
          <!-- Statut actuel -->
          <section class="consent-drawer__card">
            <div class="consent-drawer__card-head">
              <i class="tim-icons icon-chart-pie-36"></i>
              <span>Statut actuel</span>
            </div>
            <div class="consent-drawer__card-body">
              <span
                class="consent-drawer__badge"
                :class="statusConfig(selectedConsent.consent.status).pillClass"
              >
                CONSENTEMENT {{ statusConfig(selectedConsent.consent.status).labelUpper }}
              </span>
              <div class="consent-drawer__muted">
                Collecté le {{ formatIsoDateTime(selectedConsent.collection.collected_at) }}
              </div>
            </div>
          </section>

          <!-- Consommateur -->
          <section class="consent-drawer__card">
            <div class="consent-drawer__card-head">
              <i class="tim-icons icon-single-02"></i>
              <span>Consommateur</span>
            </div>
            <div class="consent-drawer__card-body">
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Nom / Prénom</span>
                <span class="consent-drawer__v">
                  {{ selectedConsent.consumer.last_name }} {{ selectedConsent.consumer.first_name }}
                </span>
              </div>
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Email</span>
                <span class="consent-drawer__v">{{ selectedConsent.consumer.email }}</span>
              </div>
              <div v-if="selectedConsent.consent.status === 'ACTIVE'" class="consent-drawer__kv">
                <span class="consent-drawer__k">Téléphone</span>
                <span class="consent-drawer__v">{{ selectedConsent.consumer.phone }}</span>
              </div>
            </div>
          </section>

          <!-- Collecte -->
          <section class="consent-drawer__card">
            <div class="consent-drawer__card-head">
              <i class="tim-icons icon-calendar-60"></i>
              <span>Collecte</span>
            </div>
            <div class="consent-drawer__card-body">
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Date et heure</span>
                <span class="consent-drawer__v">{{ formatIsoDateTime(selectedConsent.collection.collected_at) }}</span>
              </div>
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Timestamp</span>
                <span class="consent-drawer__v consent-drawer__v--mono">{{ selectedConsent.collection.collected_at }}</span>
              </div>
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Produit</span>
                <span class="consent-drawer__v">{{ selectedConsent.collection.product }}</span>
              </div>
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Type de flux</span>
                <span class="consent-drawer__v">{{ flowTypeLabel(selectedConsent.collection.flow_type) }}</span>
              </div>
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Origine du support</span>
                <span class="consent-drawer__v">
                  <img
                    v-if="isMonGustaveOrigin(selectedConsent.collection.support_origin.domain)"
                    src="/img/ico-mongustave.png"
                    width="20"
                    height="20"
                    alt="MON GUSTAVE"
                    title="MON GUSTAVE"
                    class="consent-drawer__mg-icon"
                  />
                  <a
                    v-if="selectedConsent.collection.support_origin.url"
                    class="consent-drawer__v--link"
                    :href="selectedConsent.collection.support_origin.url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ selectedConsent.collection.support_origin.domain }}
                  </a>
                  <template v-else>
                    {{ selectedConsent.collection.support_origin.domain }}
                  </template>
                </span>
              </div>
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Plateforme</span>
                <span class="consent-drawer__v">{{ selectedConsent.collection.platform_label }}</span>
              </div>
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">ID consentement</span>
                <span class="consent-drawer__v">{{ selectedConsent.consent_id }}</span>
              </div>
            </div>
          </section>

          <!-- Preuve -->
          <section class="consent-drawer__card">
            <div class="consent-drawer__card-head">
              <i class="tim-icons icon-paper"></i>
              <span>Preuve du consentement</span>
            </div>
            <div class="consent-drawer__card-body">
              <div class="consent-drawer__optin">
                « {{ selectedConsent.consent.optin_text }} »
              </div>
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Acceptation</span>
                <span class="consent-drawer__v">{{ formatIsoDateTime(selectedConsent.consent.accepted_at) }}</span>
              </div>
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Durée</span>
                <span class="consent-drawer__v">{{ selectedConsent.consent.duration_months }} mois</span>
              </div>
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Expiration</span>
                <span class="consent-drawer__v">
                  {{
                    selectedConsent.consent.status === 'WITHDRAWN'
                      ? '—'
                      : formatIsoDate(selectedConsent.consent.expires_at)
                  }}
                </span>
              </div>
            </div>
          </section>

          <!-- Gestion -->
          <section class="consent-drawer__card consent-drawer__card--last">
            <div class="consent-drawer__card-head">
              <i class="tim-icons icon-single-02"></i>
              <span>Gestion par le consommateur</span>
            </div>
            <div class="consent-drawer__card-body">
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Espace consommateur</span>
                <span
                  class="consent-drawer__v"
                  :class="consumerSpaceClass(selectedConsent)"
                >
                  {{ consumerSpaceLabel(selectedConsent) }}
                </span>
              </div>
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Retrait</span>
                <span class="consent-drawer__v">
                  {{ selectedConsent.withdrawal.withdrawn ? 'Oui' : 'Non' }}
                </span>
              </div>
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Date et heure</span>
                <span class="consent-drawer__v">
                  {{
                    selectedConsent.withdrawal.withdrawn && selectedConsent.withdrawal.withdrawn_at
                      ? formatIsoDateTime(selectedConsent.withdrawal.withdrawn_at)
                      : '—'
                  }}
                </span>
              </div>
              <div class="consent-drawer__kv">
                <span class="consent-drawer__k">Canal</span>
                <span class="consent-drawer__v">
                  {{
                    selectedConsent.withdrawal.channel
                      ? withdrawalChannelLabel(selectedConsent.withdrawal.channel)
                      : '—'
                  }}
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </aside>
  </div>
</template>

<script>
import { Select, Option, DatePicker, Table, TableColumn, Input } from 'element-ui'
import swal from 'sweetalert2'
import { BasePagination, BaseCheckbox, BaseInput } from 'src/components'
import gql from 'graphql-tag'
import { Icons } from 'src/pages/Pages/MyMarketAdmin/Product_Icons'

const STATUS_MAP = {
  ACTIVE: {
    label: 'Actif',
    labelUpper: 'ACTIF',
    pillClass: 'consent-status-pill--active'
  },
  WITHDRAWN: {
    label: 'Retiré',
    labelUpper: 'RETIRÉ',
    pillClass: 'consent-status-pill--withdrawn'
  },
  EXPIRED: {
    label: 'Expiré',
    labelUpper: 'EXPIRÉ',
    pillClass: 'consent-status-pill--expired'
  }
}

const FLOW_LABELS = {
  exclusif: 'Fiche exclusif',
  duo: 'Fiche duo',
  trio: 'Fiche trio',
  mer: 'MER'
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function formatYmd(date) {
  return date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate())
}

function currentMonthDateRange() {
  const now = new Date()
  const from = new Date(now.getFullYear(), now.getMonth(), 1)
  return {
    date_from: formatYmd(from),
    date_to: formatYmd(now)
  }
}

export default {
  name: 'ConsentementsMarketAnnonceur',
  apollo: {
    Consents: {
      client: 'MARKET',
      query: require('src/graphql/leadmarket/Annonceur/ConsentsAnnonceur.gql'),
      variables() {
        return {
          id_utilisateur: JSON.parse(localStorage.getItem('utilisateur')).id,
          date_debut: this.filters.date_from || null,
          date_fin: this.filters.date_to || null,
          produit: this.filters.product ? parseInt(this.filters.product, 10) : null,
          statut: this.filters.status || null,
          pack: this.filters.flow_type || null,
          recherche: (this.filters.search || '').trim() || null
        }
      },
      fetchPolicy: 'network-only',
      skip() {
        return this.skipQuery
      },
      update: data =>
        (data.consentsAnnonceur &&
          data.consentsAnnonceur[0] &&
          data.consentsAnnonceur[0].consents) ||
        [],
      result() {
        this.skipQuery = true
        this.searchLoading = false
        this.allItems = (this.Consents || []).map(row => this.mapConsentRow(row))
        this.applyFilters()
      },
      error(error) {
        this.skipQuery = true
        this.searchLoading = false
        const message =
          (error &&
            error.graphQLErrors &&
            error.graphQLErrors[0] &&
            error.graphQLErrors[0].message) ||
          (error && error.message) ||
          'Erreur lors du chargement des consentements'
        this.$notify({
          type: 'warning',
          verticalAlign: 'top',
          horizontalAlign: 'right',
          message
        })
      }
    },
    produits: {
      client: 'MARKET',
      query: gql`
        query {
          produits {
            pk_categorie
            designation
          }
        }
      `,
      result({ data }) {
        const list = (data && data.produits) || []
        this.filtersMeta = {
          ...this.filtersMeta,
          products: [
            { value: '', label: 'Tous' },
            ...list.map(p => ({
              value: String(p.pk_categorie),
              label: p.designation
            }))
          ]
        }
      }
    }
  },
  components: {
    BasePagination,
    BaseCheckbox,
    BaseInput,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Input.name]: Input
  },
  data() {
    return {
      kpis: null,
      allItems: [],
      filteredItems: [],
      skipQuery: true,
      searchLoading: false,
      Consents: [],
      icons: Icons,
      filtersMeta: {
        products: [{ value: '', label: 'Tous' }],
        flow_types: [
          { value: '', label: 'Tous' },
          { value: 'exclusif', label: 'Exclusif' },
          { value: 'duo', label: 'Duo' },
          { value: 'trio', label: 'Trio' },
          { value: 'mer', label: 'MER' }
        ],
        consent_statuses: [
          { value: '', label: 'Tous' },
          { value: 'ACTIVE', label: 'Actif' },
          { value: 'WITHDRAWN', label: 'Retiré' },
          { value: 'EXPIRED', label: 'Expiré' }
        ]
      },
      filters: {
        ...currentMonthDateRange(),
        product: '',
        flow_type: '',
        status: '',
        search: ''
      },
      appliedFilters: null,
      exportWithProof: false,
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [25, 50, 100, 250]
      },
      showDetailPanel: false,
      selectedConsent: null,
      withdrawLoading: false,
      withdrawingKey: null,
      sortProp: 'collected_at',
      sortOrder: 'descending'
    }
  },
  computed: {
    paginatedItems() {
      const start = (this.pagination.currentPage - 1) * this.pagination.perPage
      return this.filteredItems.slice(start, start + this.pagination.perPage)
    },
    paginationFrom() {
      return (this.pagination.currentPage - 1) * this.pagination.perPage
    },
    paginationTo() {
      const high = this.paginationFrom + this.pagination.perPage
      return Math.min(high, this.filteredItems.length)
    },
    loading() {
      return !!(
        this.searchLoading ||
        (this.$apollo.queries.Consents && this.$apollo.queries.Consents.loading)
      )
    },
    startPickerOptions() {
      return {
        disabledDate: date => this.isOutsideSystemYear(date) || this.isAfterDate(date, this.filters.date_to)
      }
    },
    endPickerOptions() {
      return {
        disabledDate: date => this.isOutsideSystemYear(date) || this.isBeforeDate(date, this.filters.date_from)
      }
    }
  },
  beforeDestroy() {
    this.setCalendlyBadgeVisible(true)
  },
  watch: {
    showDetailPanel(isOpen) {
      this.setCalendlyBadgeVisible(!isOpen)
    }
  },
  methods: {
    setCalendlyBadgeVisible(visible) {
      if (typeof document === 'undefined' || !document.body) return
      document.body.classList.toggle('consent-drawer-open', !visible)
    },
    isOutsideSystemYear(date) {
      const day = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      const today = new Date()
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      const min = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())
      return day < min || day > todayStart
    },
    isAfterDate(date, ymd) {
      if (!ymd) return false
      const end = new Date(ymd)
      const endStart = new Date(end.getFullYear(), end.getMonth(), end.getDate())
      const day = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      return day > endStart
    },
    isBeforeDate(date, ymd) {
      if (!ymd) return false
      const start = new Date(ymd)
      const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate())
      const day = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      return day < startDay
    },
    isMonGustaveOrigin(domain) {
      return String(domain || '').toLowerCase().indexOf('mongustave') !== -1
    },
    normalizeFlowType(flow) {
      const packId = parseInt(flow, 10)
      if (packId === 2) return 'trio'
      if (packId === 4) return 'duo'
      if (packId === 1) return 'exclusif'
      const key = String(flow || '')
        .toLowerCase()
        .trim()
      if (key.includes('trio')) return 'trio'
      if (key.includes('duo')) return 'duo'
      if (key.includes('exclu')) return 'exclusif'
      if (key === 'mer') return 'mer'
      return key
    },
    mapConsentRow(row) {
      return {
        consent_id: row.consent_id,
        pk_bon_commande: row.pk_bon_commande,
        consumer: {
          consumer_id: 'CONSUMER_' + row.pk_lead,
          first_name: row.prenom,
          last_name: row.nom,
          email: row.email,
          phone: row.telephone
        },
        collection: {
          collected_at: row.date_collecte || row.date_lead,
          product: row.categorie,
          product_code: String(row.pk_categorie || ''),
          flow_type: this.normalizeFlowType(row.type_flux || row.pk_pack),
          platform: row.platform || 'WEEDO_MARKET',
          platform_label: row.platform_label || 'Weedo Market',
          support_origin: {
            domain: row.support_origin || 'MonGustave.fr',
            url: row.site_collecte
          }
        },
        pk_lead: row.pk_lead,
        consent: {
          status: row.consent_status,
          optin_text: row.phrase_optin,
          accepted_at: row.date_collecte || row.date_lead,
          duration_months: row.duration_months || 12,
          expires_at: row.expires_at
        },
        withdrawal: {
          withdrawn: !!row.retrait,
          withdrawn_at: row.date_retrait,
          channel: row.canal_retrait
        },
        management: {
          consumer_space_status: row.consumer_space_status || 'ACTIVE'
        }
      }
    },
    buildKpis(list) {
      const total = list.length
      const active = list.filter(r => r.consent.status === 'ACTIVE').length
      const withdrawn = list.filter(r => r.consent.status === 'WITHDRAWN').length
      const expired = list.filter(r => r.consent.status === 'EXPIRED').length
      const pct = n => (total ? Math.round((n * 1000) / total) / 10 : 0)
      this.kpis = {
        collected: { count: total, percent: null },
        active: { count: active, percent: pct(active) },
        withdrawn: { count: withdrawn, percent: pct(withdrawn) },
        expired: { count: expired, percent: pct(expired) }
      }
    },
    refetchConsents() {
      if (!this.filters.date_from || !this.filters.date_to) {
        const range = currentMonthDateRange()
        this.filters.date_from = this.filters.date_from || range.date_from
        this.filters.date_to = this.filters.date_to || range.date_to
      }
      this.searchLoading = true
      this.skipQuery = true
      this.Consents = []
      this.allItems = []
      this.filteredItems = []
      this.kpis = null
      this.pagination.currentPage = 1
      this.closeDetailPanel()
      this.$nextTick(() => {
        this.skipQuery = false
      })
    },
    applyFilters() {
      this.appliedFilters = { ...this.filters }
      let list = [...this.allItems]
      const f = this.appliedFilters
      if (f.date_from) {
        list = list.filter(row => this.collectedDateOnly(row) >= f.date_from)
      }
      if (f.date_to) {
        list = list.filter(row => this.collectedDateOnly(row) <= f.date_to)
      }
      if (f.product) {
        list = list.filter(row => row.collection.product_code === String(f.product))
      }
      if (f.flow_type) {
        list = list.filter(row => row.collection.flow_type === f.flow_type)
      }
      if (f.status) {
        list = list.filter(row => row.consent.status === f.status)
      }
      list = this.sortItems(list)
      this.filteredItems = list
      this.buildKpis(list)
      this.pagination.currentPage = 1
    },
    resetFilters() {
      this.filters = {
        ...currentMonthDateRange(),
        product: '',
        flow_type: '',
        status: '',
        search: ''
      }
      this.refetchConsents()
    },
    sortItems(list) {
      const prop = this.sortProp
      const dir = this.sortOrder === 'ascending' ? 1 : -1
      return list.sort((a, b) => {
        let va
        let vb
        if (prop === 'collected_at') {
          va = a.collection.collected_at
          vb = b.collection.collected_at
        } else if (prop === 'status') {
          va = a.consent.status
          vb = b.consent.status
        } else if (prop === 'expires_at') {
          va = a.consent.expires_at
          vb = b.consent.expires_at
        } else if (prop === 'collection.product') {
          va = a.collection.product
          vb = b.collection.product
        } else {
          va = a.collection[prop] || ''
          vb = b.collection[prop] || ''
        }
        if (va < vb) return -1 * dir
        if (va > vb) return 1 * dir
        return 0
      })
    },
    onSortChange({ prop, order }) {
      if (!order) return
      this.sortProp = prop || 'collected_at'
      this.sortOrder = order
      this.filteredItems = this.sortItems([...this.filteredItems])
    },
    openDetail(row) {
      this.selectedConsent = row
      this.showDetailPanel = true
    },
    closeDetailPanel() {
      this.showDetailPanel = false
    },
    canWithdrawConsent(row) {
      return (
        row &&
        row.consent &&
        row.consent.status === 'ACTIVE' &&
        !!row.pk_bon_commande &&
        !!row.pk_lead
      )
    },
    withdrawRowKey(row) {
      if (!row) return ''
      return String(row.pk_bon_commande || '') + '_' + String(row.pk_lead || '')
    },
    isWithdrawingRow(row) {
      return this.withdrawLoading && this.withdrawingKey === this.withdrawRowKey(row)
    },
    confirmWithdrawConsent(row) {
      if (!this.canWithdrawConsent(row) || this.withdrawLoading) return
      swal
        .fire({
          text: 'Vous êtes sur le point de faire un retrait de ce consentement. Confirmez-vous ?',
          width: 400,
          showCancelButton: true,
          confirmButtonColor: '#988fc3',
          cancelButtonColor: '#27293D',
          confirmButtonText: 'Oui, retirer',
          cancelButtonText: 'Annuler'
        })
        .then(result => {
          if (result.value) {
            this.withdrawConsent(row)
          }
        })
    },
    withdrawConsent(row) {
      const accessToken = localStorage.getItem('token_jwt')
      const utilisateur = JSON.parse(localStorage.getItem('utilisateur') || '{}')
      this.withdrawLoading = true
      this.withdrawingKey = this.withdrawRowKey(row)
      this.axios
        .post(
          'https://apileadmarket.wee-do-it.net/api/retrait-consentement',
          //'http://localhost:8000/api/retrait-consentement',
          {
            id_utilisateur: utilisateur.id || '',
            pk_bon_commande: row.pk_bon_commande,
            pk_lead: row.pk_lead,
            date_collecte: (row.collection && row.collection.collected_at) || '',
            duration_months: (row.consent && row.consent.duration_months) || 12,
            type_flux: (row.collection && row.collection.flow_type) || '',
            pk_categorie: (row.collection && row.collection.product_code) || '',
            email: (row.consumer && row.consumer.email) || '',
            source: 'espace-consentements'
          },
          {
            headers: { authorization: `Bearer ${accessToken}` }
          }
        )
        .then(res => {
          const data = (res && res.data) || {}
          if (!data.success) {
            throw new Error((data && data.message) || 'Retrait impossible')
          }
          this.applyWithdrawnLocally(row, data)
          this.$notify({
            type: 'success',
            verticalAlign: 'top',
            horizontalAlign: 'right',
            message: 'Consentement retiré'
          })
        })
        .catch(error => {
          const message =
            (error && error.response && error.response.data && error.response.data.message) ||
            (error && error.message) ||
            'Erreur lors du retrait'
          this.$notify({
            type: 'warning',
            verticalAlign: 'top',
            horizontalAlign: 'right',
            message
          })
        })
        .finally(() => {
          this.withdrawLoading = false
          this.withdrawingKey = null
        })
    },
    applyWithdrawnLocally(row, data) {
      const dateRetrait = (data && data.date_retrait) || new Date().toISOString()
      const canal = (data && data.canal_retrait) || 'espace-consentements'
      const patch = item => {
        if (!item || String(item.pk_bon_commande) !== String(row.pk_bon_commande)) return item
        return {
          ...item,
          consent: {
            ...item.consent,
            status: 'WITHDRAWN'
          },
          withdrawal: {
            withdrawn: true,
            withdrawn_at: dateRetrait,
            channel: canal
          }
        }
      }
      this.allItems = this.allItems.map(patch)
      if (this.selectedConsent && String(this.selectedConsent.pk_bon_commande) === String(row.pk_bon_commande)) {
        this.selectedConsent = patch(this.selectedConsent)
      }
      this.applyFilters()
    },
    onExportCsv() {
      const cols = [
        'consent_id',
        'date_collecte',
        'nom',
        'prenom',
        'email',
        'telephone',
        'produit',
        'type_flux',
        'origine',
        'plateforme',
        'statut',
        'expiration',
        'retrait',
        'date_retrait',
        'pk_bon_commande'
      ]
      if (this.exportWithProof) {
        cols.push('optin', 'acceptation', 'duree_mois')
      }
      const lines = [cols.join(';')]
      this.filteredItems.forEach(item => {
        const row = [
          item.consent_id,
          item.collection.collected_at,
          item.consumer.last_name,
          item.consumer.first_name,
          item.consumer.email,
          item.consumer.phone,
          item.collection.product,
          (item.collection.flow_type || '').toUpperCase(),
          item.collection.support_origin.domain,
          item.collection.platform_label,
          this.statusConfig(item.consent.status).label,
          item.consent.status === 'WITHDRAWN' ? '—' : item.consent.expires_at || '',
          item.withdrawal.withdrawn ? 'Oui' : 'Non',
          item.withdrawal.withdrawn_at || '',
          item.pk_bon_commande
        ]
        if (this.exportWithProof) {
          row.push(item.consent.optin_text, item.consent.accepted_at, item.consent.duration_months)
        }
        lines.push(row.map(v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"').join(';'))
      })
      const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const fileLink = document.createElement('a')
      fileLink.href = window.URL.createObjectURL(blob)
      fileLink.setAttribute('download', 'consentements.csv')
      document.body.appendChild(fileLink)
      fileLink.click()
      fileLink.remove()
      this.alertExportDownload(lines.length - 1)
    },
    alertExportDownload(nb) {
      const accessToken = localStorage.getItem('token_jwt')
      const utilisateur = JSON.parse(localStorage.getItem('utilisateur') || '{}')
      const f = this.appliedFilters || this.filters
      const product = this.filtersMeta.products.find(
        p => String(p.value) === String(f.product)
      )
      const statusOpt = this.filtersMeta.consent_statuses.find(
        s => String(s.value) === String(f.status || '')
      )
      const formData = new FormData()
      formData.append('id_utilisateur', utilisateur.id || '')
      formData.append('nb', nb)
      formData.append('date_debut', f.date_from || '')
      formData.append('date_fin', f.date_to || '')
      formData.append('produit', product && product.value ? product.label : '')
      formData.append('pack', f.flow_type || '')
      formData.append('statut', (statusOpt && statusOpt.label) || f.status || '')
      formData.append('recherche', f.search || '')
      formData.append('preuve', this.exportWithProof ? '1' : '0')
      this.axios
        .post('https://apileadmarket.wee-do-it.net/api/alert-export-consentements', formData, {
          headers: { authorization: `Bearer ${accessToken}` }
        })
        .catch(() => {})
    },
    statusConfig(status) {
      return (
        STATUS_MAP[status] || {
          label: status,
          labelUpper: String(status || '').toUpperCase(),
          pillClass: 'consent-status-pill--unknown'
        }
      )
    },
    flowTypeLabel(flow) {
      return (FLOW_LABELS[flow] || flow || '').toUpperCase()
    },
    withdrawalChannelLabel(channel) {
      if (channel === 'espace-client') return 'Espace consommateur'
      return channel
    },
    consumerSpaceStatus(row) {
      const status =
        (row.management && row.management.consumer_space_status) ||
        (row.withdrawal && row.withdrawal.consumer_space_status) ||
        'ACTIVE'
      return status
    },
    consumerSpaceLabel(row) {
      const status = this.consumerSpaceStatus(row)
      if (status === 'ACTIVE') return 'Actif'
      if (status === 'INACTIVE') return 'Inactif'
      return status
    },
    consumerSpaceClass(row) {
      const status = this.consumerSpaceStatus(row)
      if (status === 'ACTIVE') return 'consent-drawer__v--success'
      if (status === 'INACTIVE') return 'consent-drawer__v--danger'
      return ''
    },
    formatNumber(n) {
      if (n == null) return '0'
      return Number(n).toLocaleString('fr-FR')
    },
    collectedDateOnly(row) {
      const iso = row.collection.collected_at || ''
      return iso.slice(0, 10)
    },
    formatCollectedDate(row) {
      const iso = row.collection.collected_at
      if (!iso) return '—'
      const d = new Date(iso)
      if (isNaN(d.getTime())) return iso.slice(0, 10)
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yyyy = d.getFullYear()
      return `${dd}/${mm}/${yyyy}`
    },
    formatCollectedTime(row) {
      const iso = row.collection.collected_at
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      const h = String(d.getHours()).padStart(2, '0')
      const m = String(d.getMinutes()).padStart(2, '0')
      const s = String(d.getSeconds()).padStart(2, '0')
      return `${h}:${m}:${s}`
    },
    formatPhone(phone) {
      const digits = String(phone || '').replace(/\D/g, '')
      if (!digits) return phone || ''
      return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim()
    },
    formatIsoDate(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (isNaN(d.getTime())) return '—'
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yyyy = d.getFullYear()
      return `${dd}/${mm}/${yyyy}`
    },
    formatIsoDateTime(iso) {
      if (!iso) return '—'
      return `${this.formatIsoDate(iso)} ${this.formatCollectedTime({ collection: { collected_at: iso } })}`
    }
  }
}
</script>

<style scoped lang="scss">
.btn-consent {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 18px;
  margin: 0;
  border: none;
  border-radius: 0.4285rem;
  background: #5b6abf !important;
  color: #fff !important;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: none;
  &:hover,
  &:focus,
  &:active {
    background: #4c5aab !important;
    color: #fff !important;
    outline: 0;
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  i {
    margin-right: 6px;
  }
  &--icon {
    width: 32px;
    height: 32px;
    min-width: 32px;
    padding: 0;
    border-radius: 50%;
    i {
      margin-right: 0;
      font-size: 12px;
    }
  }
}

.consent-filter-actions {
  display: flex;
  align-items: center;
  &__btns {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
  }
  .btn {
    height: 40px;
    margin: 0 8px 0 0;
    padding-top: 0;
    padding-bottom: 0;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    white-space: nowrap;
  }
}

.consentements-subtitle {
  font-size: 0.95rem;
  max-width: 720px;
}

.consent-kpi {
  height: 100%;
  border: none;
  border-radius: 8px;
  color: #fff;
  .card-body {
    padding: 1rem 1.25rem;
  }
  &__label {
    min-height: 2.4em;
    font-size: 0.75rem;
    text-transform: uppercase;
    opacity: 0.95;
    margin-bottom: 0.35rem;
  }
  &__value {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
  }
  &__pct {
    margin-top: 0.2rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.72);
  }
  &--collected {
    background: linear-gradient(135deg, #2b3277 0%, #588bbd 100%);
  }
  &--active {
    background: linear-gradient(135deg, #2dce89 0%, #24a46d 100%);
  }
  &--withdrawn {
    background: linear-gradient(135deg, #f5365c 0%, #d63447 100%);
  }
  &--expired {
    background: linear-gradient(135deg, #fb6340 0%, #fbb140 100%);
  }
}

.consentements-table .cell {
  word-break: break-word;
}

.consentements-table .status_photo {
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    max-height: 38px;
    width: auto;
  }
}

.consent-mg-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}

.consent-platform-icon {
  width: 40px !important;
  height: 40px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  svg {
    width: 46px;
    height: 46px;
  }
}

.consent-consumer-name,
.consent-consumer-email,
.consent-consumer-phone {
  font-size: 95% !important;
}

.consent-consumer-name,
.consent-consumer-email {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.consent-consumer-phone {
  font-weight: 700 !important;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.consent-status-pill {
  display: inline-block;
  min-width: 76px;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-align: center;
  color: #fff;
  line-height: 1.15;
  border: none;
  &--btn {
    cursor: pointer;
    outline: none;
    position: relative;
    z-index: 1;
    box-shadow: 0 0 0 0 transparent;
    transition: background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease,
      filter 0.15s ease;
    &:hover {
      background-color: #15a872;
      filter: none;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(28, 201, 138, 0.45), 0 0 0 2px rgba(28, 201, 138, 0.35);
    }
    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(28, 201, 138, 0.45);
    }
    &:active {
      transform: translateY(0) scale(0.97);
      box-shadow: 0 1px 3px rgba(28, 201, 138, 0.35);
    }
    &:disabled,
    &.is-loading {
      cursor: wait;
      opacity: 0.85;
      transform: none;
      box-shadow: none;
      pointer-events: none;
    }
  }
  &__spinner {
    font-size: 0.85rem;
    line-height: 1;
    color: #fff;
  }
  &--active {
    background-color: #1cc98a;
  }
  &--withdrawn {
    background-color: #f54275;
  }
  &--expired {
    background-color: #ff9f76;
  }
  &--unknown {
    background-color: #8898aa;
  }
}

.consent-drawer-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1040;
  background: rgba(8, 10, 20, 0.55);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.25s ease, visibility 0.25s ease;
  &.is-open {
    opacity: 1;
    visibility: visible;
  }
}

.consent-drawer {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 1050;
  width: 440px;
  max-width: 100%;
  height: 100vh;
  background: #12121c;
  box-shadow: -12px 0 32px rgba(0, 0, 0, 0.45);
  transform: translateX(100%);
  transition: transform 0.28s ease;
  display: flex;
  flex-direction: column;
  color: #fff;
  &.is-open {
    transform: translateX(0);
  }
  &__content {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    flex-shrink: 0;
  }
  &__header-left {
    display: flex;
    align-items: center;
    min-width: 0;
    padding-right: 10px;
  }
  &__header-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin-right: 10px;
    border-radius: 8px;
    background: #6c5ce7;
    flex-shrink: 0;
    i {
      font-size: 12px;
      color: #fff;
    }
  }
  &__eyebrow {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #fff;
    line-height: 1.2;
  }
  &__title {
    margin-top: 2px;
    font-size: 12px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.45);
    word-break: break-word;
  }
  &__close {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: transparent;
    color: rgba(255, 255, 255, 0.85);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    padding: 0;
    i {
      font-size: 12px;
    }
    &:hover {
      color: #fff;
      border-color: rgba(255, 255, 255, 0.4);
    }
  }
  &__scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 12px 14px 16px;
  }
  &__card {
    background: #1a1a28;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    margin-bottom: 10px;
    overflow: hidden;
  }
  &__card--last {
    margin-bottom: 0;
  }
  &__card-head {
    display: flex;
    align-items: center;
    padding: 10px 14px 4px;
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    i {
      font-size: 13px;
      margin-right: 8px;
      color: rgba(255, 255, 255, 0.85);
    }
  }
  &__card-body {
    padding: 2px 14px 12px;
  }
  &__badge {
    display: inline-block;
    padding: 7px 12px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #fff;
    margin-bottom: 6px;
  }
  &__muted {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.45);
  }
  &__kv {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }
  &__k {
    flex: 0 0 40%;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.45);
    line-height: 1.35;
  }
  &__v {
    flex: 1;
    text-align: right;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    line-height: 1.35;
    word-break: break-word;
  }
  &__mg-icon {
    display: inline-block;
    vertical-align: middle;
    margin-right: 6px;
  }
  &__v--mono {
    font-weight: 500;
    font-size: 11px;
  }
  &__v--link {
    color: #4ea1ff;
    font-weight: 600;
  }
  &__v--success {
    color: #1cc98a;
    font-weight: 700;
  }
  &__v--danger {
    color: #f54275;
    font-weight: 700;
  }
  &__optin {
    background: rgba(0, 0, 0, 0.28);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 8px;
    font-size: 12px;
    line-height: 1.45;
    color: rgba(255, 255, 255, 0.88);
    font-style: italic;
  }
}
</style>

<!-- Hover lignes : bleu transparent (maquette) -->
<style lang="scss">
.consentements-page .el-date-editor.el-input,
.consentements-page .el-select {
  width: 100%;
}

.consentements-page .btn-consent {
  background: #5b6abf !important;
  color: #fff !important;
}
.consentements-page .btn-consent:hover,
.consentements-page .btn-consent:focus,
.consentements-page .btn-consent:active {
  background: #4c5aab !important;
  color: #fff !important;
}

.consentements-table.el-table table.el-table__body > tbody > tr {
  cursor: pointer;
}
.consentements-table.el-table table.el-table__body > tbody > tr:hover > td,
.consentements-table.el-table table.el-table__body > tbody > tr:hover > td.el-table__cell,
.consentements-table.el-table table.el-table__body > tbody > tr.hover-row > td,
.consentements-table.el-table table.el-table__body > tbody > tr.hover-row > td.el-table__cell {
  background-color: rgba(88, 139, 189, 0.18) !important;
}

/* Masquer le badge Calendly tant que le panneau preuve est ouvert */
body.consent-drawer-open .calendly-badge-widget {
  display: none !important;
}
</style>
